/**
 * The `BrowserDetector` class provides methods and properties to detect the current browser,
 * its version, and whether it is supported or belongs to certain categories such as mobile
 * or specific browser families.
 */
export default class BrowserDetector {
  private browser: {
    name: string;
    version: string;
  };

  private readonly unsupportedBrowsers: {
    Chrome: number;
    Firefox: number;
    IE: number;
    Edge: number;
    Opera: number;
    Safari: number
  };

  constructor() {
    this.browser = {
      name: '',
      version: '',
    };
    this.unsupportedBrowsers = {
      Chrome: 132,
      Firefox: 130,
      IE: 10,
      Edge: 130,
      Opera: 111,
      Safari: 15,
    };

    this._detectBrowser();
  }

  /**
   * Detects the current browser and its version number.
   *
   * @returns An object with keys for browser `name` and `version`.
   */
  _detectBrowser() {
    this.browser = (function () {
      let ua = navigator.userAgent,
        tem,
        M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

      if (/trident/i.test(M[1])) {
        tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
        return { name: "IE", version: tem[1] || "" };
      }

      if (M[1] === "Chrome") {
        tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
        if (tem != null) {
          return { name: tem[1].replace("OPR", "Opera"), version: tem[2] };
        }
      }

      M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];

      if ((tem = ua.match(/version\/(\d+)/i)) != null) {
        M.splice(1, 1, tem[1]);
      }

      return { name: M[0], version: M[1] };
    })();
  }

  /**
   * Checks if the current browser is Internet Explorer.
   */
  get isIE() {
    return this.browser.name === "IE";
  }

  /**
   * Checks if the current browser is Edge.
   */
  get isEdge() {
    return this.browser.name === "Edge";
  }

  /**
   * Checks if the current browser is from Microsoft (Edge
   * or Internet Explorer).
   */
  get isMicrosoft() {
    return this.isIE || this.isEdge;
  }

  /**
   * Checks if the current browser is Firefox.
   */
  get isFirefox() {
    return this.browser.name === "Firefox";
  }

  /**
   * Checks if the current browser is Chrome.
   */
  get isChrome() {
    return this.browser.name === "Chrome";
  }

  /**
   * Checks if the current browser is Safari.
   */
  get isSafari() {
    return this.browser.name === "Safari";
  }

  /**
   * Checks if the current browser is from an Android device.
   */
  get isAndroid() {
    return /Android/i.test(navigator.userAgent);
  }

  /**
   * Checks if the current browser is from a BlackBerry device.
   */
  get isBlackBerry() {
    return /BlackBerry/i.test(navigator.userAgent);
  }

  /**
   * Checks if the current browser is from a Windows Mobile device.
   */
  get isWindowsMobile() {
    return /IEMobile/i.test(navigator.userAgent);
  }

  /**
   * Checks if the current browser is Mobile Safari.
   */
  get isIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /**
   * Checks if the current browser is a mobile browser.
   */
  get isMobile() {
    return this.isAndroid || this.isBlackBerry || this.isWindowsMobile || this.isIOS;
  }

  /**
   * Checks if the current browser is supported by
   * our environment.
   */
  isSupported() {
    if (this.unsupportedBrowsers.hasOwnProperty(this.browser.name)) {
      if (+this.browser.version > this.unsupportedBrowsers[this.browser.name]) {
        return true;
      }
    }

    return false;
  }
}
